// Reporting's Over Time section (owner, 2026-11-04: "a section of Reports,
// called Over Time, which are line charts with counts by like Invoices By
// Month, Invoices By Week, and Invoices By Date. Topics should include
// Invoices, Spend, Median Handling Time, Flags, and Corrections").
//
// EIGHTEEN CHARTS: six measures by three grains, every one drawn from one read,
// `GET /analytics/over-time?grain=` (`docs/api.md` §16.10), which carries all
// six measures per period. Five are over the same invoices, those uploaded in
// the period; Exports counts the invoices exported in it (owner, 2026-11-04:
// "I need Exports over time ... counts of invoices export by date, week, and
// month"), by the Exported Date (D-440).
//
// No I/O here: `OverTimeView` does the read and hands the answer in.
import { OVER_TIME_GRAIN, type OverTimeGrain } from '@ap/shared'
import type { AnalyticsOverTime, AnalyticsOverTimePeriod } from '../api/types'
import type { BarRowData } from '../components/DashboardCharts'
import { formatCurrency } from './format'
import { compactMoney, wholeNumber } from './reportCharts'
import { SECTION_COLORS } from './theme'

type OverTimeMeasure = 'invoices' | 'spend' | 'handling' | 'flags' | 'corrections' | 'exports'

export interface OverTimeChart {
  id: string
  /** The rail's section heading. */
  group: 'Over Time'
  title: string
  grain: OverTimeGrain
  measure: OverTimeMeasure
  /** The heading over the period column in the exported table. */
  labelHeader: string
  /** The heading over the value column in the exported table. `Spend` is what
      makes the Excel column money (`chartExport.ts`). */
  valueHeader: string
}

/** One point of a line: a period and its value. `value` is null where the
    measure has nothing to say (a handling-time median with no sessions). */
export interface OverTimePoint {
  key: string
  /** The period in full, for a hover title and the exported table: "Week of Oct 12, 2026". */
  label: string
  /** The period short, for the axis: "Oct 12". */
  tick: string
  value: number | null
  display: string
  title: string
}

const whole = wholeNumber
/** `45 s` under a minute, `2m 05s` above it: `seconds` in `reportCharts.ts`, which
    does not export it. */
const seconds = (value: number) => {
  const total = Math.round(value)
  if (total < 60) return `${total} s`
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`
}

const MEASURES: ReadonlyArray<{
  measure: OverTimeMeasure
  topic: string
  valueHeader: string
  value: (p: AnalyticsOverTimePeriod) => number | null
  show: (value: number) => string
  /** The hover title's value in full. */
  full: (value: number) => string
}> = [
  {
    measure: 'invoices',
    topic: 'Invoices',
    valueHeader: 'Invoices',
    value: (p) => p.invoice_count,
    show: whole,
    full: (n) => `${n} invoice${n === 1 ? '' : 's'} uploaded`,
  },
  {
    measure: 'spend',
    topic: 'Spend',
    valueHeader: 'Spend',
    value: (p) => p.total,
    show: compactMoney,
    full: (n) => `${formatCurrency(n)} on invoices uploaded`,
  },
  {
    // NULL, NEVER ZERO, where no invoice in the period has a session: zero
    // would claim instant handling nobody measured. The line breaks there.
    measure: 'handling',
    topic: 'Median Handling Time',
    valueHeader: 'Median Handling Seconds',
    value: (p) => p.median_handling_seconds,
    show: seconds,
    full: (n) => `${seconds(n)} median handling time per invoice uploaded`,
  },
  {
    measure: 'flags',
    topic: 'Flags',
    valueHeader: 'Flags',
    value: (p) => p.flag_count,
    show: whole,
    full: (n) => `${n} flag${n === 1 ? '' : 's'} on invoices uploaded`,
  },
  {
    measure: 'corrections',
    topic: 'Corrections',
    valueHeader: 'Corrections',
    value: (p) => p.correction_count,
    show: whole,
    full: (n) => `${n} correction${n === 1 ? '' : 's'} on invoices uploaded`,
  },
  {
    // Keyed on the Export Control click, not the upload (D-440).
    measure: 'exports',
    topic: 'Exports',
    valueHeader: 'Exports',
    value: (p) => p.export_count,
    show: whole,
    full: (n) => `${n} invoice${n === 1 ? '' : 's'} exported`,
  },
]

const GRAINS: ReadonlyArray<{ grain: OverTimeGrain; noun: string; header: string }> = [
  { grain: OVER_TIME_GRAIN.month, noun: 'Month', header: 'Month' },
  { grain: OVER_TIME_GRAIN.week, noun: 'Week', header: 'Week' },
  { grain: OVER_TIME_GRAIN.day, noun: 'Date', header: 'Date' },
]

/** Title Case keeps "by" lowercase, as "Accounts by Spend" does. */
export const OVER_TIME_CHARTS: readonly OverTimeChart[] = MEASURES.flatMap((m) =>
  GRAINS.map((g) => ({
    id: `over-time-${m.measure}-${g.grain}`,
    group: 'Over Time' as const,
    title: `${m.topic} by ${g.noun}`,
    grain: g.grain,
    measure: m.measure,
    labelHeader: g.header,
    valueHeader: m.valueHeader,
  })),
)

/** The line's colour: the section's, whatever the measure (`SECTION_COLORS`). */
export const OVER_TIME_COLOR = SECTION_COLORS.overTime

/** The Over Time chart with `id`, or undefined when `id` names a bar chart. */
export function overTimeChartById(id: string): OverTimeChart | undefined {
  return OVER_TIME_CHARTS.find((chart) => chart.id === id)
}

// A period start is a calendar date, so it is read and printed in UTC: no zone
// may move the 1st of a month to the 30th.
const monthLong = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
const monthShort = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
const dayLong = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
const dayShort = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
const asDate = (period: string) => new Date(`${period}T00:00:00Z`)

/** "October 2026", "Week of Oct 12, 2026", "Oct 12, 2026". */
function periodLabel(period: string, grain: OverTimeGrain): string {
  if (grain === OVER_TIME_GRAIN.month) return monthLong.format(asDate(period))
  if (grain === OVER_TIME_GRAIN.week) return `Week of ${dayLong.format(asDate(period))}`
  return dayLong.format(asDate(period))
}

/** "Oct 2026", "Oct 12", "Oct 12". */
function periodTick(period: string, grain: OverTimeGrain): string {
  return grain === OVER_TIME_GRAIN.month ? monthShort.format(asDate(period)) : dayShort.format(asDate(period))
}

/** How the chart's value axis is labelled, and whether it steps by whole numbers (a count does). */
export function overTimeAxis(chart: OverTimeChart): { format: (value: number) => string; wholeOnly: boolean } {
  const m = MEASURES.find((entry) => entry.measure === chart.measure) as (typeof MEASURES)[number]
  return { format: m.show, wholeOnly: chart.measure !== 'spend' && chart.measure !== 'handling' }
}

/** The chart's points, oldest first, one per period the answer carries. */
export function overTimePoints(chart: OverTimeChart, data: AnalyticsOverTime): OverTimePoint[] {
  const m = MEASURES.find((entry) => entry.measure === chart.measure) as (typeof MEASURES)[number]
  return data.periods.map((p) => {
    const value = m.value(p)
    const label = periodLabel(p.period_start, chart.grain)
    return {
      key: p.period_start,
      label,
      tick: periodTick(p.period_start, chart.grain),
      value,
      display: value === null ? '—' : m.show(value),
      title: value === null ? `${label}: no handling time recorded` : `${label}: ${m.full(value)}`,
    }
  })
}

/**
 * The rows Download writes (`chartExport.ts`), OLDEST FIRST — time, not rank,
 * so no sort: label = the period, total = the value, display = the value as the
 * chart prints it. A period with no value (a handling-time median nobody
 * measured) is left out rather than written as 0, which would read as instant.
 */
export function overTimeRows(chart: OverTimeChart, data: AnalyticsOverTime): BarRowData[] {
  return overTimePoints(chart, data)
    .filter((point): point is OverTimePoint & { value: number } => point.value !== null)
    .map((point) => ({
      key: point.key,
      label: point.label,
      total: point.value,
      display: point.display,
      segments: [{ color: OVER_TIME_COLOR, value: point.value }],
      title: point.title,
    }))
}
